import styled, { DefaultTheme } from "styled-components";
import { Card, Typography, Button, Space, Table, Progress, Input, Tabs } from "antd";

const { Title, Text } = Typography;

declare module "styled-components" {
  export interface DefaultTheme {
    mode?: "light" | "dark";
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

export const MainContainer = styled.div`
  padding: 32px;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  background: ${(props) => getThemeValue(props, "#1A202C", "#F7FAFC")} !important;
  min-height: 100vh;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${(props) => getThemeValue(props, "#4A5568", "#E2E8F0")};
`;

export const BackButton = styled(Button)`
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 12px rgba(0, 0, 0, 0.3)",
    "0 2px 8px rgba(0, 0, 0, 0.1)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  
  &:hover {
    transform: translateX(-1px);
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 6px 16px rgba(0, 0, 0, 0.4)",
      "0 4px 12px rgba(0, 0, 0, 0.15)"
    )};
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PageTitle = styled(Title)`
  margin: 0 !important;
  /* background: linear-gradient(135deg, #722ed1 0%, #1890ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; */
  /* background-clip: text; */
  font-weight: 700 !important;
  letter-spacing: -0.5px;
`;

export const PageSubtitle = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const FilterCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.8;
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
  }

  .ant-card-body {
    padding: 28px;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const FilterLabel = styled(Text)`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const SearchInput = styled(Input)`
  max-width: 300px;
`;

export const SummaryCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  margin-bottom: 24px;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #722ed1 0%, #1890ff 50%, #fa8c16 100%);
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
    transform: translateY(-1px);
  }

  .ant-card-body {
    padding: 28px;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SummaryLabel = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const SummaryValue = styled(Text)`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${(props) => getThemeValue(props, "#4A5568", "#E2E8F0")};
  margin: 16px 0;
`;

export const ItemCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  margin-bottom: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 2px 8px rgba(0, 0, 0, 0.2)",
    "0 2px 8px rgba(0, 0, 0, 0.06)"
  )};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 12px 32px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3)",
      "0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.08)"
    )};
    transform: translateY(-1px);
    border-color: ${(props) => getThemeValue(props, "#4A5568", "rgba(24, 144, 255, 0.3)")};

    &::before {
      opacity: 1;
    }
  }

  .ant-card-body {
    padding: 24px;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const ItemRank = styled(Text)`
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, #1890ff 0%, #722ed1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-right: 12px;
  letter-spacing: -0.5px;
`;

export const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

export const ItemName = styled(Text)`
  font-size: 16px;
  font-weight: 600;
`;

export const BadgeContainer = styled(Space)`
  margin-top: 4px;
`;

export const TrendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TrendValue = styled(Text)<{ trend: "up" | "down" | "stable" }>`
  font-size: 14px;
  color: ${(props) => {
    switch (props.trend) {
      case "up":
        return "#52c41a";
      case "down":
        return "#ff4d4f";
      default:
        return "#8c8c8c";
    }
  }} !important;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StatLabel = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const StatValue = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.3px;
  transition: all 0.2s ease;
`;

export const ProgressContainer = styled.div`
  margin-bottom: 16px;
`;

export const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

export const StyledProgress = styled(Progress)`
  .ant-progress-bg {
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 100%);
    border-radius: 4px;
  }
  
  .ant-progress-inner {
    border-radius: 4px;
    overflow: hidden;
  }
`;

export const GrowthContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => getThemeValue(props, "#4A5568", "#E2E8F0")};
`;

export const StyledTable = styled(Table)<{ theme?: DefaultTheme }>`
  .ant-table {
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, "#4A5568", "#fafafa")} !important;
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, "#4A5568", "#f0f0f0")};
  }

  .ant-table-tbody > tr > td {
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, "#4A5568", "#f0f0f0")};
  }

  .ant-table-tbody > tr:hover > td {
    background: ${(props) => getThemeValue(props, "#4A5568", "#fafafa")} !important;
  }
`;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
    padding: 0 4px;
  }

  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
    font-weight: 500;
    padding: 12px 20px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 8px 8px 0 0;
    
    &:hover {
      color: ${(props) => getThemeValue(props, "#ffffff", "#722ed1")} !important;
      background: ${(props) => getThemeValue(props, "rgba(114, 46, 209, 0.1)", "rgba(114, 46, 209, 0.05)")};
    }
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: #722ed1 !important;
      font-weight: 600;
    }
  }

  .ant-tabs-ink-bar {
    background: linear-gradient(90deg, #722ed1 0%, #1890ff 100%);
    height: 3px;
    border-radius: 2px;
  }
`;

export const TabContent = styled.div`
  padding-top: 32px;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const PeriodCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  margin-top: 24px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.6;
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
    transform: translateY(-1px);
  }

  .ant-card-body {
    padding: 28px;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const PeriodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const PeriodItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PeriodLabel = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const PeriodValue = styled(Text)`
  font-size: 14px;
  font-weight: 500;
`;

export const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Additional styled components for inline styles
export const FullWidthSpace = styled(Space)`
  width: 100%;
`;

export const FilterTitle = styled(Title)`
  margin-bottom: 16px !important;
`;

export const MaxWidthInput = styled(Input)`
  max-width: 300px;
`;

export const NoMarginFilterLabel = styled(FilterLabel)`
  margin-bottom: 0 !important;
`;

export const LoadingContainer = styled.div`
  padding: 40px;
  text-align: center;
`;

export const TabTitle = styled(Title)`
  margin: 0 !important;
`;

export const ItemHeaderFlex = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex: 1;
`;

export const StatItemEnd = styled(StatItem)`
  align-items: flex-end;
`;

export const RevenueStatValue = styled(StatValue)`
  color: #52c41a !important;
`;

export const SmallTrendValue = styled(TrendValue)`
  font-size: 14px;
  font-weight: 600;
`;

export const TrendingUpSummaryValue = styled(SummaryValue)`
  color: #52c41a !important;
  font-size: 24px !important;
  font-weight: bold !important;
`;

export const TrendingDownSummaryValue = styled(SummaryValue)`
  color: #ff4d4f !important;
  font-size: 24px !important;
  font-weight: bold !important;
`;

export const MediumSummaryValue = styled(SummaryValue)`
  font-size: 18px !important;
  font-weight: 600 !important;
`;

export const SummaryItemEnd = styled(SummaryItem)`
  align-items: flex-end;
`;

export const RankText = styled(Text)`
  color: #1890ff !important;
`;

export const RevenueText = styled(Text)`
  color: #52c41a !important;
`;

export const SmallText = styled(Text)`
  font-size: 12px;
`;

export const SmallProgress = styled(Progress)`
  width: 60px;
`;

export const VolumeGrowthText = styled(Text)`
  font-size: 12px;
  color: #52c41a !important;
  display: block;
`;

export const RevenueGrowthText = styled(Text)`
  font-size: 12px;
  color: #1890ff !important;
  display: block;
`;

export const PeriodTitle = styled(Title)`
  margin-bottom: 0 !important;
`;

export const IconWrapper = styled.span<{ color?: string }>`
  color: ${(props) => props.color || "#8c8c8c"};
`;
